import { useAuth0 } from "@auth0/auth0-react";
import { IconButton, TableContainer } from "@material-ui/core";
import { withStyles } from "@material-ui/core/styles";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import AuthService from "Lib/API/Auth";
import Action from "components/ActionPopOver";
import AlertDialog from "components/Dialog";
import Loader from "components/loader/Loader";
import Button from "components/button/Button";
import SearchComp from "components/Searchcomponent";
import SideMenuList from "components/sideMenuList/SideMenuList";
import React, { useEffect, useMemo, useState } from "react";
import { toastr } from "react-redux-toastr";
import SettingHeader from "../../SettingHeader/settingHEader";
import "../../users/style.scss";
import "./Tags.scss";

const TYPES = [
  { value: "account", label: "Accounts" },
  { value: "contact", label: "Contacts" },
  { value: "notes", label: "Notes" },
];

const plural = { account: "accounts", contact: "contacts", notes: "notes" };
const singular = { account: "account", contact: "contact", notes: "note" };

const TableCellBody = withStyles(() => ({
  root: {
    height: "61px",
    padding: "0px 40px",
  },
}))(TableCell);

const actionCol = {
  width: 56,
  whiteSpace: "nowrap",
  padding: "0 8px",
};

const relatedName = (type, count) =>
  count === 1 ? singular[type] : plural[type];

const confirmText = (action, tag, count, nextTitle) => {
  const name = relatedName(tag.type, count || 2);
  if (!count) {
    return `Delete "${tag.title}"? It is not used on any ${plural[tag.type]}.`;
  }
  const place = count === 1 ? `that ${name}` : `those ${name}`;
  if (action === "delete") {
    return `"${tag.title}" is used on ${count} ${name}. Deleting it will also delete it from ${place}.`;
  }
  return `Rename "${tag.title}" to "${nextTitle}"? It is used on ${count} ${name}. Renaming it will also rename it on ${place}.`;
};

const errMsg = (err) => err?.data?.message || "Something went wrong";

const Tags = ({ heading }) => {
  const { getAccessTokenSilently } = useAuth0();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("account");
  const [draft, setDraft] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [confirm, setConfirm] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const token = await getAccessTokenSilently();
      const types = ["account", "contact", "notes"];
      const responses = await Promise.all(
        types.map((type) => AuthService.getTags(type, "", "", token))
      );
      const list = responses.flatMap((res, i) =>
        (res?.result || []).map((tag) => ({
          ...tag,
          type: tag.type || types[i],
        }))
      );
      setRows(list);
    } catch (err) {
      toastr.error("Error", errMsg(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const selectType = (item) => {
    if (item.value === typeFilter) return;
    cancelEdit();
    setConfirm(null);
    setTypeFilter(item.value);
  };

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = rows.filter((row) => {
      if (row.type !== typeFilter) return false;
      if (q && !(row.title || "").toLowerCase().includes(q)) return false;
      return true;
    });
    list.sort((a, b) => (a.title || "").localeCompare(b.title || ""));
    return list;
  }, [rows, search, typeFilter]);

  const usageFor = async (tag) => {
    setChecking(true);
    try {
      const token = await getAccessTokenSilently();
      const res = await AuthService.getTagUsage(tag._id, token);
      return res?.result?.count || 0;
    } finally {
      setChecking(false);
    }
  };

  const startEdit = (tag) => {
    setEditingId(tag._id);
    setDraft(tag.title || "");
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft("");
  };

  const runUpdate = async (tag, title) => {
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      await AuthService.updateTag({ id: tag._id, title }, token);
      toastr.success("Success", "Tag updated");
      setConfirm(null);
      cancelEdit();
      await load();
    } catch (err) {
      const message = errMsg(err);
      toastr.error(
        "Error",
        message === "tag title already exists"
          ? `You cannot use "${title}". That tag name already exists.`
          : message
      );
    } finally {
      setBusy(false);
    }
  };

  const runDelete = async (tag) => {
    setBusy(true);
    try {
      const token = await getAccessTokenSilently();
      await AuthService.deleteOrgTag({ id: tag._id }, token);
      toastr.success("Success", "Tag deleted");
      setConfirm(null);
      if (editingId === tag._id) cancelEdit();
      await load();
    } catch (err) {
      toastr.error("Error", errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const askRename = async (tag) => {
    const title = draft.trim();
    if (!title) return toastr.error("Error", "Please enter a tag name");
    if (title === tag.title) return cancelEdit();
    const nameTaken = rows.some(
      (row) =>
        row._id !== tag._id &&
        row.type === tag.type &&
        (row.title || "").trim() === title
    );
    if (nameTaken) {
      return toastr.error(
        "Error",
        `You cannot use "${title}". That tag name already exists.`
      );
    }
    setBusy(true);
    try {
      const count = await usageFor(tag);
      if (!count) {
        await runUpdate(tag, title);
        return;
      }
      setConfirm({
        action: "rename",
        tag,
        title,
        text: confirmText("rename", tag, count, title),
      });
    } catch (err) {
      toastr.error("Error", errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const askDelete = async (tag) => {
    setBusy(true);
    try {
      const count = await usageFor(tag);
      setConfirm({
        action: "delete",
        tag,
        text: confirmText("delete", tag, count),
      });
    } catch (err) {
      toastr.error("Error", errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const onAccept = () => {
    if (!confirm?.tag || busy) return;
    if (confirm.action === "delete") runDelete(confirm.tag);
    else runUpdate(confirm.tag, confirm.title);
  };

  return (
    <div className="w-100">
      <SettingHeader
        heading={heading}
        parentHeading="Members"
        path="/financial-setting/members/tags"
        alertOnEdit={false}
      />
      <div className="main-setting__wrapper tags-page">
        <div className="setting-main">
          <div className="left-menu-div">
            <SideMenuList
              list={TYPES}
              selected={typeFilter}
              onClick={selectType}
            />
          </div>
          <div className="settings-details tags-details">
        <div className="tags-board">
        <div className="tags-board__bar">
          <SearchComp
            value={search}
            setSearchText={(e) => setSearch(e.target.value)}
            placeholder="Search"
            style={{ width: "20.5rem", backgroundColor: "#fff" }}
          />
        </div>
        <div className="user__table__container">
          <TableContainer className="scroll-table">
            <Table aria-label="tags">
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <div className="user__loader">
                        <Loader />
                      </div>
                    </TableCell>
                  </TableRow>
                ) : visible.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center">
                      <h6 style={{ padding: "80px 0" }}>No tags found</h6>
                    </TableCell>
                  </TableRow>
                ) : (
                  visible.map((row) => (
                    <TableRow key={row._id}>
                      <TableCellBody
                        className="table__body__cell"
                        align="left"
                        colSpan={editingId === row._id ? 2 : 1}
                        style={editingId === row._id ? { paddingRight: 8 } : undefined}
                      >
                        {editingId === row._id ? (
                          <div className="d-flex align-items-center" style={{ gap: 8, width: "100%" }}>
                            <input
                              className="form-control"
                              value={draft}
                              autoFocus
                              style={{ height: 36, minWidth: 0, flex: 1 }}
                              onChange={(e) => setDraft(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === "Enter") askRename(row);
                                if (e.key === "Escape") cancelEdit();
                              }}
                            />
                            <Button
                              size="sm"
                              variant="primary-outline"
                              onClick={cancelEdit}
                              disabled={busy}
                            >
                              Cancel
                            </Button>
                            <Button
                              size="sm"
                              variant="primary"
                              onClick={() => askRename(row)}
                              disabled={busy}
                            >
                              Save
                            </Button>
                          </div>
                        ) : (
                          <span className="tags-tag-cell">{row.title}</span>
                        )}
                      </TableCellBody>
                      {editingId !== row._id && (
                      <TableCellBody
                        className="table__body__cell"
                        align="left"
                        style={actionCol}
                      >
                          <IconButton className="p-0" style={{ width: 28, height: 28 }}>
                            <Action
                              action="class"
                              style={{ width: "16px", height: "16px" }}
                              onEditClick={() => startEdit(row)}
                              onClassDeleteClick={() => askDelete(row)}
                            />
                          </IconButton>
                      </TableCellBody>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
        </div>
          </div>
        </div>
      </div>
      {checking && (
        <div className="tags-usage-loader">
          <Loader />
        </div>
      )}
      <AlertDialog
        open={Boolean(confirm)}
        onDecline={() => {
          if (!busy) setConfirm(null);
        }}
        onAccept={onAccept}
        title="Are you sure?"
        description={confirm?.text}
        disabled={busy}
      />
    </div>
  );
};

export default Tags;
